import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { Plus, ChevronRight, Check, Trash2 } from 'lucide-react';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useCompanyProfile, useUpdateCompanyField } from '@/hooks/useCompanyProfile';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { CultureSkeleton } from '@/components/company/skeletons';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import StepTipsPanel from '@/components/company/StepTipsPanel';
import { useAutosave } from '@/hooks/useAutosave';
import DraftBanner from '@/components/form/DraftBanner';
import AutosaveIndicator from '@/components/form/AutosaveIndicator';

const cultureItemSchema = z.object({
  budaya: z.string().min(1, 'Nama budaya wajib diisi'),
  deskripsi: z.string().min(1, 'Deskripsi wajib diisi'),
});

const cultureFormSchema = z.object({
  cultures: z.array(cultureItemSchema).min(3, 'Minimal 3 budaya harus diisi'),
  is_published_culture: z.boolean(),
});

type CultureItem = z.infer<typeof cultureItemSchema>;
type CultureFormValues = z.infer<typeof cultureFormSchema>;

const defaultCultureItem: CultureItem = { budaya: '', deskripsi: '' };

const budayaPlaceholders = ['Contoh: Berkualitas', 'Contoh: Ramah', 'Contoh: Cepat', 'Contoh: Profesionalisme'];

const deskripsiPlaceholders = [
  'Contoh: Menyajikan kuliner dari bahan terpilih yang bersih dan bergizi',
  'Contoh: Pelayanan dengan wajah ceria dan etika yang baik',
  'Contoh: Proses efisien tanpa mengurangi kualitas',
  'Contoh: Hasil kerja dengan standar tinggi dan tanggung jawab',
];

export default function Culture() {
  const formControl = useForm<CultureFormValues>({
    resolver: zodResolver(cultureFormSchema),
    defaultValues: {
      cultures: [defaultCultureItem, defaultCultureItem, defaultCultureItem],
      is_published_culture: false,
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = formControl;
  const { fields, append, remove } = useFieldArray({ control, name: 'cultures' });
  const formRef = useRef<HTMLFormElement>(null);

  const isReadOnly = useIsReadOnly();
  const { currentTenant } = useTenantState();
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const { companyProfile } = useCompanyState();
  const { isLoading } = useCompanyProfile(currentTenant?.id);
  const updateFieldMutation = useUpdateCompanyField();
  const [showPanel, setShowPanel] = useState(true);
  // Gates autosave until the form holds server data, so the empty defaults
  // present before reset() are never written over a real draft.
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const autosave = useAutosave({
    form: formControl,
    formKey: 'culture',
    enabled: isDataLoaded && !isReadOnly,
    // Server-owned publish flag; the page reloads it and a stale draft value
    // must not flip it back.
    exclude: ['is_published_culture'],
  });

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const cultureStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'culture');
  const videoLink = cultureStep?.video_link;
  const tips = (cultureStep as { step_name: string; video_link?: string; tips?: string } | undefined)?.tips;

  useEffect(() => {
    if (!companyProfile) {
      setIsDataLoaded(true);
      return;
    }

    const cultureValues = companyProfile.culture_values as CultureItem[] | null;

    if (cultureValues && Array.isArray(cultureValues) && cultureValues.length > 0) {
      const cultures = [...cultureValues];
      while (cultures.length < 3) {
        cultures.push(defaultCultureItem);
      }
      reset({ cultures, is_published_culture: !!companyProfile.is_published_culture });
    } else {
      reset({
        cultures: [defaultCultureItem, defaultCultureItem, defaultCultureItem],
        is_published_culture: !!companyProfile.is_published_culture,
      });
    }

    setIsDataLoaded(true);
  }, [companyProfile, reset]);

  const handleSave = async (data: CultureFormValues) => {
    if (!currentTenant?.id) return;

    const validCultures = data.cultures.filter(c => c.budaya.trim() || c.deskripsi.trim());

    if (validCultures.length < 3) {
      toast.error('Minimal 3 budaya harus diisi');
      return;
    }

    // Offline: the request would fail and lose the edits, so hold them locally
    // and let the user retry once the connection is back.
    if (!autosave.isOnline) {
      autosave.flush({ pendingSync: true });
      toast.info('Anda sedang offline. Perubahan disimpan sebagai draft di perangkat ini.');
      return;
    }

    await updateFieldMutation.mutateAsync({
      tenantId: currentTenant.id,
      field: 'culture',
      value: validCultures,
      isPublished: data.is_published_culture,
    });

    // Server now holds these values; the draft would only be noise on return.
    autosave.clearDraft();
    toast.success('Budaya perusahaan berhasil disimpan');
    navigate(`/${tenant_id}/business-goal`);
  };

  const MAX_CULTURES = 10;

  const handleAddCulture = () => {
    if (fields.length >= MAX_CULTURES) {
      toast.warning('Maksimal 10 budaya');
      return;
    }
    append(defaultCultureItem);
  };

  const handleRemoveCulture = (index: number) => {
    if (fields.length > 3) {
      remove(index);
    } else {
      toast.warning('Minimal 3 budaya harus ada');
    }
  };

  if (isLoading) {
    return <CultureSkeleton />;
  }

  const isSaving = updateFieldMutation.isLoading;

  return (
    <section className="w-full dark:bg-[#0D0D0D] min-h-screen dark:text-white">
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg dark:text-white">Culture</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Anda akan merancang budaya perusahaan yang mudah dan aplikatif untuk pedoman rekrutmen dan internalisasi
            karyawan.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isReadOnly && <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />}

          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {autosave.pendingDraft && (
            <DraftBanner
              savedAt={autosave.pendingDraft.savedAt}
              pendingSync={autosave.pendingDraft.pendingSync}
              onRestore={autosave.restoreDraft}
              onDiscard={autosave.discardDraft}
            />
          )}

          <FormProvider {...formControl}>
            <form ref={formRef} onSubmit={handleSubmit(handleSave)}>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-[#1A1A1A]">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Daftar nilai budaya perusahaan (Harus diisi minimal 3 dan maksimal 10 budaya).
                </p>

                <div className="overflow-x-auto pb-1">
                  <div className="min-w-[480px]">
                    {/* Column headers */}
                    <div className="grid grid-cols-[2.5rem_1fr_2fr_2rem] gap-3 mb-2 px-1">
                      <div />
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Nama Budaya</span>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Deskripsi</span>
                      <div />
                    </div>

                    {/* Rows */}
                    <div className="space-y-2">
                      {fields.map((field, index) => (
                        <div key={field.id} className="grid grid-cols-[2.5rem_1fr_2fr_2rem] gap-3 items-center">
                          <span className="text-sm font-bold text-blue-600 dark:text-blue-400 text-center">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <input
                            {...formControl.register(`cultures.${index}.budaya`)}
                            type="text"
                            placeholder={budayaPlaceholders[index] || 'Nama budaya'}
                            disabled={isReadOnly}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                          />
                          <input
                            {...formControl.register(`cultures.${index}.deskripsi`)}
                            type="text"
                            placeholder={deskripsiPlaceholders[index] || 'Deskripsi singkat budaya'}
                            disabled={isReadOnly}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveCulture(index)}
                            disabled={fields.length <= 3 || isReadOnly}
                            className="p-1 rounded transition-colors text-red-400 hover:text-red-600 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
                            title={fields.length <= 3 ? 'Minimal 3 budaya harus ada' : 'Hapus budaya'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {errors.cultures && typeof errors.cultures.message === 'string' && (
                  <p className="text-sm text-red-500 mt-3">{errors.cultures.message}</p>
                )}

                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={handleAddCulture}
                    disabled={fields.length >= MAX_CULTURES}
                    className="mt-4 flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus size={14} />
                    Tambah Budaya
                  </button>
                )}
              </div>
            </form>
          </FormProvider>
        </div>

        {showPanel && (
          <div className="w-full xl:w-[420px] 2xl:w-[600px] shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} darkBackground />
          </div>
        )}
      </div>
    </section>
  );
}
